/**
 * Moteur de la calculatrice collège (type TI-Collège Plus) — module PUR,
 * zéro React, zéro eval : parser récursif descendant sur une liste de TOKENS.
 *
 * Le composant pousse des tokens (une touche = un token), jamais une chaîne
 * brute — c'est ce qui rend DEL trivial (retirer le dernier token, `√(`
 * part d'un coup) et le parser insensible aux ambiguïtés de découpage.
 *
 * Vocabulaire : '0'…'9', ',', '+', '−' (U+2212), '×' (U+00D7), '÷' (U+00F7),
 * '(', ')', 'π', '√(' (UN SEUL token : la touche √ ouvre sa parenthèse,
 * comme la TI), '²', 'Ans', 'cos(' et 'arccos(' (un token chacun, comme √ ;
 * touches montrées sur les seules questions qui les demandent, 1090). Le
 * mapping clavier physique ('-' → '−', '*' → '×'…) vit dans le composant,
 * jamais ici.
 *
 * Grammaire (priorités : ² > moins unaire > × ÷ > + −) :
 *   Expression := Terme (('+' | '−') Terme)*
 *   Terme      := Facteur (('×' | '÷')? Facteur)*   // op absent = × implicite (2π5)
 *   Facteur    := '−' Facteur | Postfixe             // −3² = −9 ; (−3)² = 9
 *   Postfixe   := Primaire ('²')*
 *   Primaire   := Nombre | 'π' | 'Ans' | '(' Expression [')'] | '√(' Expression [')']
 *               | 'cos(' Expression [')'] | 'arccos(' Expression [')']
 *   Nombre     := chiffre+ (',' chiffre*)? | ',' chiffre+   // ',5' lu 0,5
 *
 * cos et arccos en DEGRÉS, comme la calculatrice du collège réglée en mode
 * DEG (cours du prof, 19Cosinus : « on saisit arccos(0,8) sur la
 * calculatrice », « cos(90°) = 0 »). cos d'un multiple de 90° est exact : 0,
 * 1 ou −1 pile, jamais 6×10⁻¹⁷.
 *
 * Fermeture de parenthèses IMPLICITE en fin de tokens (comme les vraies
 * calculatrices) : `(2+3×(4` puis = → 14. Une ')' excédentaire → syntaxe.
 */

// Codes d'erreur stables → libellés affichés (l'écran montre le libellé,
// la logique ne compare que les codes).
export const ERREURS = {
    syntaxe: 'Erreur de syntaxe',
    div_zero: 'Division par zéro impossible',
    racine_negatif: 'Racine d\'un nombre négatif',
    depassement: 'Nombre trop grand',
    hors_domaine: 'Impossible : il faut un nombre entre −1 et 1',
};

const CHIFFRES = new Set(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']);
// Tokens qui ouvrent une parenthèse (la fonction avec) : √, cos, arccos.
const FONCTIONS = new Set(['√(', 'cos(', 'arccos(']);
// Tokens qui DÉMARRENT un Facteur : leur présence sans opérateur devant
// déclenche la multiplication implicite (2π5 = 2×π×5 ; 5cos(30) = 5×cos(30)).
const DEBUT_FACTEUR = new Set(['(', ...FONCTIONS, 'π', 'Ans', ',', '−', ...CHIFFRES]);

/** cos d'un angle en degrés ; exact sur les multiples de 90° (cos(90) = 0 pile). */
const cosDegres = (x) => {
    const r = ((x % 360) + 360) % 360;
    if (r === 0) return 1;
    if (r === 90 || r === 270) return 0;
    if (r === 180) return -1;
    return Math.cos((x * Math.PI) / 180);
};

class ErreurCalc extends Error {
    constructor(code) { super(code); this.code = code; }
}

/**
 * Évalue une liste de tokens. `ans` = valeur de la touche Ans.
 * @returns {{ valeur: number|null, erreur: null|'syntaxe'|'div_zero'|'racine_negatif'|'depassement'|'hors_domaine' }}
 */
export const evaluerTokens = (tokens, ans = 0) => {
    let i = 0;
    const courant = () => tokens[i];

    const lireNombre = () => {
        let entier = '';
        let decimales = null;
        while (CHIFFRES.has(courant())) { entier += tokens[i]; i += 1; }
        if (courant() === ',') {
            i += 1;
            decimales = '';
            while (CHIFFRES.has(courant())) { decimales += tokens[i]; i += 1; }
            if (courant() === ',') throw new ErreurCalc('syntaxe'); // 2e virgule du même nombre
        }
        if (entier === '' && (decimales === null || decimales === '')) throw new ErreurCalc('syntaxe');
        return Number(`${entier || '0'}.${decimales || '0'}`);
    };

    const lirePrimaire = () => {
        const t = courant();
        if (t === undefined) throw new ErreurCalc('syntaxe');
        if (t === 'π') { i += 1; return Math.PI; }
        if (t === 'Ans') { i += 1; return ans; }
        if (t === '(' || FONCTIONS.has(t)) {
            i += 1;
            const v = lireExpression();
            // ')' consommée si présente ; fin de tokens = fermeture implicite
            if (courant() === ')') i += 1;
            else if (courant() !== undefined) throw new ErreurCalc('syntaxe');
            if (t === '√(') {
                if (v < 0) throw new ErreurCalc('racine_negatif');
                return Math.sqrt(v);
            }
            if (t === 'cos(') return cosDegres(v);
            if (t === 'arccos(') {
                if (v < -1 || v > 1) throw new ErreurCalc('hors_domaine');
                return (Math.acos(v) * 180) / Math.PI;
            }
            return v;
        }
        if (CHIFFRES.has(t) || t === ',') return lireNombre();
        throw new ErreurCalc('syntaxe');
    };

    const lirePostfixe = () => {
        let v = lirePrimaire();
        while (courant() === '²') { v *= v; i += 1; }
        return v;
    };

    const lireFacteur = () => {
        if (courant() === '−') { i += 1; return -lireFacteur(); }
        return lirePostfixe();
    };

    const lireTerme = () => {
        let v = lireFacteur();
        for (;;) {
            const t = courant();
            if (t === '×' || t === '÷') {
                i += 1;
                const d = lireFacteur();
                if (t === '÷') {
                    if (d === 0) throw new ErreurCalc('div_zero');
                    v /= d;
                } else v *= d;
            } else if (t !== undefined && t !== ')' && t !== '+' && t !== '−' && DEBUT_FACTEUR.has(t)) {
                v *= lireFacteur(); // multiplication implicite
            } else return v;
        }
    };

    const lireExpression = () => {
        let v = lireTerme();
        for (;;) {
            const t = courant();
            if (t === '+') { i += 1; v += lireTerme(); }
            else if (t === '−') { i += 1; v -= lireTerme(); }
            else return v;
        }
    };

    try {
        if (tokens.length === 0) throw new ErreurCalc('syntaxe');
        const valeur = lireExpression();
        // Reste non consommé (')' orpheline, token inattendu) → syntaxe
        if (i !== tokens.length) throw new ErreurCalc('syntaxe');
        if (!Number.isFinite(valeur)) throw new ErreurCalc('depassement');
        return { valeur, erreur: null };
    } catch (e) {
        if (e instanceof ErreurCalc) return { valeur: null, erreur: e.code };
        return { valeur: null, erreur: 'syntaxe' };
    }
};

/**
 * number → écriture française d'écran : purge du bruit flottant binaire
 * (0,1+0,2 → 0,3 et pas 0,30000000000000004), 10 chiffres significatifs
 * max, virgule décimale, moins U+2212, -0 normalisé. Hors [1e-9, 1e10[ →
 * notation `m×10^n` (chaîne plate ; le composant rend l'exposant en <sup>).
 */
export const formaterResultat = (valeur) => {
    if (!Number.isFinite(valeur)) return ERREURS.depassement;
    // Purge (12 significatifs = 2 de marge au-dessus de l'affichage, comme
    // les calculatrices physiques qui calculent sur 13 digits pour 10 affichés)
    let v = Number(valeur.toPrecision(12));
    if (Object.is(v, -0)) v = 0;
    const abs = Math.abs(v);
    if (v !== 0 && (abs >= 1e10 || abs < 1e-9)) {
        const [mantisse, exposant] = v.toExponential(8).split('e');
        const m = String(Number(mantisse)).replace('.', ',').replace('-', '−');
        return `${m}×10^${Number(exposant)}`;
    }
    return String(Number(v.toPrecision(10))).replace('.', ',').replace('-', '−');
};

/** tokens → ligne d'expression de l'écran (['4','2',',','5','×','π'] → "42,5×π"). */
export const afficherTokens = (tokens) => tokens.join('');
